import React from "react";
import { isEmpty } from "lodash";
import { useLocalStore, useObserver } from "mobx-react";
import {
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableCell,
  TableBody,
  Fab,
  makeStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Add } from "@material-ui/icons";
import { store } from "@store";
import {
  CustomerContractSnapshotEditorLocation,
  CONTRACT_CONTRACT_SNAPSHOT_SLIDER
} from "@navigation";
import { CustomerContractSnapshotListItemFragment } from "@graphql";
import { SpinnerOverlay } from "@common";
import { ContractTableStore } from "./SnapshotsTableStore";
import { SnapshotRow } from "./SnapshotRow";
import { UploadSignedContractPopover } from "./UploadSignedContractPopover/UploadSignedContractPopover";
import { ArchiveRow } from "@consultants/ConsultantContract/ContractsTable/consultant-contract-table-row";
import { CollapsibleRow } from "@consultants/ConsultantContract/ContractsTable/CollapsibleRow";
import { openBlank } from "@clientUtils";

type Props = {
  id: string;
};

export const SnapshotsTable: React.FC<Props> = ({ id: CustomerContractId }) => {
  const classes = useStyles({});
  const tableStore = useLocalStore(() => new ContractTableStore(CustomerContractId));

  const [isOpenUploadPopover, setOpenUploadPopover] = React.useState<boolean>(false);
  const [uploadSignedAnchor, setUploadSignedAnchor] =
    React.useState<HTMLButtonElement | null>(null);
  const clearUpload = () => tableStore.resetUpload();
  const handleDownload = (link: string) => tableStore.download(link);

  const openUploadPopover = (
    id: string,
    ref: React.RefObject<any>,
    row: CustomerContractSnapshotListItemFragment
  ) => {
    setUploadSignedAnchor(ref?.current);
    tableStore.contractIdToUpload = id;
    tableStore.contractToUpload = row;
    setOpenUploadPopover(true);
  };

  const closeUploadPopover = () => {
    setUploadSignedAnchor(null);
    tableStore.resetUpload();
    setOpenUploadPopover(false);
  };

  const handleEdit = (id: string) => {
    store().navigation.open(
      CONTRACT_CONTRACT_SNAPSHOT_SLIDER,
      new CustomerContractSnapshotEditorLocation(CustomerContractId, id, "edit")
    );
  };

  const handleDuplicate = (id: string) => {
    store().navigation.open(
      CONTRACT_CONTRACT_SNAPSHOT_SLIDER,
      new CustomerContractSnapshotEditorLocation(CustomerContractId, id, "duplicate")
    );
  };

  // TODO add for child contracts
  // const handleCreateFrom = (id: string) => {
  //   store().navigation.open(
  //     CONSULTANT_CONTRACT_SLIDER,
  //     new ConsultantContractEditorLocation(CustomerContractSnapshotId, id, "forConfirmation")
  //   );
  // };

  const handleDelete = (id: string) => {
    store().ui.confirmDelete(locale.deleteContract, (v) => {
      if (v) {
        tableStore.deleteFromList(id);
      }
    });
  };

  const handleAdd = () => {
    store().navigation.open(
      CONTRACT_CONTRACT_SNAPSHOT_SLIDER,
      new CustomerContractSnapshotEditorLocation(CustomerContractId, null, "add")
    );
  };

  const handleNullify = (id: string, value: boolean) => {
    store().ui.confirmNullify(`${value ? locale.Nullify : locale.UnNullify} ?`, (v) => {
      if (v) {
        tableStore.handleNullify(value, id);
      }
    });
  };

  const renderList = (list: CustomerContractSnapshotListItemFragment[]) => {
    if (isEmpty(list)) {
      return (
        <TableRow>
          <TableCell colSpan={4} style={{ textAlign: "center" }}>
            {locale.NoContracts}
          </TableCell>
        </TableRow>
      );
    }

    return list.map((row) => {
      const parent = (
        <SnapshotRow
          key={row._id}
          data={row}
          disabled={false}
          open={false}
          loadingGenerate={tableStore.loadingGenerate}
          onGenerate={() => tableStore.handleGenerate(row._id)}
          onDownload={() => handleDownload(row.fileUrl)}
          onDelete={() => handleDelete(row._id)}
          onUpload={(ref) => openUploadPopover(row._id, ref, row)}
          onEdit={() => handleEdit(row._id)}
          onDuplicate={() => handleDuplicate(row._id)}
          onNullify={(value) => handleNullify(row._id, value)}
          onPublish={() => null}
          onShowInOneflow={() => openBlank(row.publishedUrl)}
        />
      );

      // TODO add child contracts
      // if (row.ChildContracts?.length > 0) {
      //   return (
      //     <CollapsibleRow
      //       childContracts={row.ChildContracts}
      //       component={parent}
      //       renderList={renderList}
      //     />
      //   );
      // }

      return parent;
    });
  };

  return useObserver(() => (
    <>
      <TableContainer component={Paper} className={classes.tableWrap}>
        <SpinnerOverlay show={tableStore.loading} />
        <Table
          aria-label={"snapshots"}
          className={classes.table}
          // padding={"none"}
          size={"small"}
        >
          <TableHead>
            <TableRow>
              {/*next row reserved for collaps arrow*/}
              <TableCell style={{ width: 30 }} />
              <TableCell style={{ width: "auto" }}>{locale.Name}</TableCell>
              {/*actions*/}
              <TableCell style={{ width: 200 }} />
              <TableCell align={"right"} className={classes.actions}>
                <Fab
                  onClick={handleAdd}
                  color="primary"
                  // disabled={!tableStore.canAddContract}
                  size={"small"}
                >
                  <Add />
                </Fab>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderList(tableStore.actualItems)}

            {!isEmpty(tableStore.pastItems) && (
              <CollapsibleRow
                renderList={renderList}
                component={<ArchiveRow title={locale.Archived} />}
                defaultOpen={false}
                childContracts={tableStore.pastItems}
                childPadding={"0"}
                childBg={"#fff"}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isOpenUploadPopover && (
        <UploadSignedContractPopover
          anchorEl={uploadSignedAnchor}
          onClose={closeUploadPopover}
          onClear={clearUpload}
          onSubmit={() => {
            setUploadSignedAnchor(null);
            setOpenUploadPopover(false);
          }}
          store={tableStore}
        />
      )}
    </>
  ));
};

const useStyles = makeStyles(() => ({
  tableWrap: {
    position: "relative",
    overflowX: "hidden",
    padding: 0
  },
  table: {
    tableLayout: "fixed",
    minHeight: 110
  },
  actions: {
    width: 160
  }
}));
